import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  getNotifications,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  Notification,
} from '../../services/notificationService';
import NotificationItem from './NotificationItem';
import './NotificationsDropdown.css';

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  unreadCount: number;
  onNotificationRead: () => void;
}

const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  isOpen,
  onClose,
  unreadCount,
  onNotificationRead,
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [unreadFilter, setUnreadFilter] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load notifications when dropdown is opened
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setLoading(true);
        const response = await getNotifications(page, 10, unreadFilter);

        if (page === 1) {
          setNotifications(response.data);
        } else {
          setNotifications((prev) => [...prev, ...response.data]);
        }

        setHasMore(response.pagination.page < response.pagination.pages);
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, page, unreadFilter]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle mark as read
  const handleMarkAsRead = async (id: string) => {
    try {
      await markAsRead(id);

      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) =>
          notification._id === id ? { ...notification, isRead: true } : notification
        )
      );

      onNotificationRead();
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();

      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => ({ ...notification, isRead: true }))
      );

      onNotificationRead();
    } catch (err) {
      console.error('Error marking all notifications as read:', err);
    }
  };

  // Handle delete notification
  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);

      setNotifications((prevNotifications) =>
        prevNotifications.filter((notification) => notification._id !== id)
      );

      if (unreadCount > 0) {
        onNotificationRead();
      }
    } catch (err) {
      console.error('Error deleting notification:', err);
    }
  };

  // Load more notifications
  const handleLoadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  // Toggle unread filter
  const toggleUnreadFilter = () => {
    setUnreadFilter((prev) => !prev);
    setPage(1);
  };

  if (!isOpen) return null;

  return (
    <div className="notifications-dropdown" ref={dropdownRef}>
      <div className="notifications-header">
        <h3>Notifications</h3>
        <div className="notifications-actions">
          <button className="toggle-unread-btn" onClick={toggleUnreadFilter}>
            {unreadFilter ? 'Show All' : 'Unread Only'}
          </button>
          <button
            className="mark-all-read-btn"
            onClick={handleMarkAllAsRead}
            disabled={!notifications.some((n) => !n.isRead)}
          >
            Mark All Read
          </button>
        </div>
      </div>

      <div className="notifications-content">
        {loading && page === 1 ? (
          <div className="notifications-loading">Loading notifications...</div>
        ) : error ? (
          <div className="notifications-error">{error}</div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <p>No notifications to display</p>
            {unreadFilter && (
              <button className="show-all-btn" onClick={toggleUnreadFilter}>
                Show All Notifications
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="notifications-list">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification._id}
                  notification={notification}
                  onMarkAsRead={handleMarkAsRead}
                  onDelete={handleDelete}
                />
              ))}
            </div>

            {hasMore && (
              <div className="load-more">
                <button className="load-more-btn" onClick={handleLoadMore} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div className="notifications-footer">
        <Link to="/notifications" className="view-all-btn" onClick={onClose}>
          View All Notifications
        </Link>
        <button className="close-btn" onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
};

export default NotificationsDropdown;
